import React, { useRef, useState } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import { Alert, Box, Button, CircularProgress, Divider, Grid, IconButton, Paper, Tab, Tabs, Typography } from "@mui/material";

import FileCopyIcon from '@mui/icons-material/FileCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { DataLayout, DrawerControl, FieldControl, FileUpload, Log, StatusChip } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { useHandlePreSign } from "../../../hooks/useHandlePreSign";
import { useEditArtefacts, useFetchArtefactsDetails } from "../mutation";
import { FileUploadRef } from "../../../shared/components/FileUpload";
import { CreateArtefacts } from "./CreateArtefacts";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const ArtefactsDetails = ({ projectId, artefactId, refetchArtefacts }: { projectId: string | undefined, artefactId: string | null, refetchArtefacts: () => void }) => {

    const { can } = useCapabilities();

    const isAllowedEditArtefact = can("operational_delivery:project:artefact:edit");

    const editArtefacts = useEditArtefacts();
    const { handleDownload } = useHandlePreSign({});
    const { data: artefactDetails, isLoading: isPending, refetch } = useFetchArtefactsDetails(artefactId);

    const [currentTab, setCurrentTab] = useState('overview');
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    const fileUploadRef = useRef<FileUploadRef | null>(null);
    const [expiryDate, setExpiryDate] = useState<null | Date>(null);

    const [editStatus, setEditStatus] = useState<{ status: boolean; id: string | null, projectId: string | null }>({
        status: false,
        id: null,
        projectId: null
    });

    const onApproved = async (payload: any) => {
        if (artefactId) {
            await editArtefacts.mutateAsync({ payload: payload, artefactId });
            refetch();
            refetchArtefacts();
            setExpiryDate(null);
        }
    };

    const onUpdateVersion = async () => {
        if (fileUploadRef.current && artefactId) {
            fileUploadRef.current.setId(artefactId);
            setTimeout(() => {
                fileUploadRef.current?.uploadAllFiles();
            }, 0);
        };
    };

    const isCurrent = (file_id: string) => {
        const latestFile = artefactDetails?.files.reduce((prev: { uploaded_date: string | number | Date; }, current: { uploaded_date: string | number | Date; }) => {
            return (new Date(prev.uploaded_date) > new Date(current.uploaded_date)) ? prev : current
        });
        return latestFile.file_id === file_id;
    }

    if (isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>
            <TabContext value={currentTab}>
                <Box display="flex" alignItems="center" alignContent="center">
                    <Tabs
                        value={currentTab}
                        onChange={handleChangeTab}
                        aria-label="Tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: "auto" }}
                    >
                        <Tab
                            sx={{ height: "auto", minHeight: "auto" }}
                            label={'Overview'}
                            value={'overview'}
                        />
                        <Tab
                            sx={{ height: "auto", minHeight: "auto" }}
                            label={'versions'}
                            value={'versions'}
                        />
                        <Tab
                            sx={{ height: "auto", minHeight: "auto" }}
                            label={'log'}
                            value={'log'}
                        />
                    </Tabs>
                </Box>
                <Divider />
                <Box flex={1} overflow="hidden">
                    <TabPanel value="overview" sx={{ p: 2, height: "100%" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                            <Box display={'flex'} flex={1} flexDirection={'column'} gap={2} sx={{ overflowY: 'auto' }}>
                                <DataLayout title="Artefact Details" bgVariant="filled">
                                    <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                                        {
                                            [
                                                { label: 'Artefact ID', value: artefactDetails.artefact_ref },
                                                { label: 'Artefact Title', value: artefactDetails.title },
                                                { label: 'Artefact Description', value: artefactDetails.description },
                                                { label: 'Category', value: artefactDetails.category },
                                                { label: 'Project ID', value: artefactDetails.project_id },
                                                { label: 'Stage gate', value: artefactDetails.stage_gate_id },
                                                { label: 'Assigned to', value: artefactDetails.assigned_to },
                                                { label: 'Date Submitted', value: formatDate(artefactDetails.created_at) },
                                                { label: 'Current Version', value: 'v1' },
                                            ].map((value) => (
                                                <Grid container>
                                                    <Grid size={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {value.label}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={6}>
                                                        <Typography variant="body2" color="text.primary">
                                                            {value.value}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            ))
                                        }
                                    </Box>
                                </DataLayout>
                                <Alert severity="error" sx={{ my: 2 }}>
                                    <Typography variant="body1">
                                        {
                                            artefactDetails.is_expired ?
                                                'Artefact Expired' : 'Artefact Not Expired'
                                        }
                                    </Typography>
                                    <Typography variant="subtitle2">
                                        {
                                            artefactDetails.is_expired ?
                                                `Expired on ${artefactDetails.expiry_date ? formatDate(artefactDetails.expiry_date) : 'N/A'}` :
                                                `Expire on ${artefactDetails.expiry_date ? formatDate(artefactDetails.expiry_date) : 'N/A'}`
                                        }
                                    </Typography>
                                </Alert>
                            </Box>
                            <Box display={'flex'} flexShrink={0} justifyContent={'flex-end'} gap={2}>
                                {
                                    isAllowedEditArtefact && (
                                        <Button variant="text" onClick={() => { setEditStatus({ status: true, id: artefactDetails.id, projectId: artefactDetails.project_id }) }}>Edit Artefact</Button>
                                    )
                                }
                                <Button variant="contained"
                                    onClick={() => onApproved({ "status": "approved" })}
                                    disabled={artefactDetails.status === 'approved' || editArtefacts.isPending}
                                    loading={editArtefacts.isPending}
                                >
                                    {artefactDetails.status === 'approved' ? 'Approved' : 'Approve Artefact'}
                                </Button>
                            </Box>
                        </Box>
                    </TabPanel>
                    <TabPanel value="versions" sx={{ p: 2, height: "100%" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                            <Box display={'flex'} flex={1} flexDirection={'column'} gap={2} sx={{ overflowY: 'auto' }}>
                                {
                                    artefactDetails?.files?.length === 0 ?
                                        <Alert severity="info">
                                            No File has been uploaded yet
                                        </Alert> :
                                        artefactDetails?.files?.map((value: { file_id: string, file_type: string, file_name: string; created_date: string | Date | null; version_id: string; s3_path: string; assigned_to: string; expiry_date: string | Date | null; is_expired: boolean }) => (
                                            <Paper
                                                data-background-elevation={1}
                                                variant="outlined"
                                                sx={{ p: 2 }}
                                            >
                                                <Box display={'flex'} alignItems={'center'} gap={2}>
                                                    <FileCopyIcon sx={{ fontSize: 30 }} />
                                                    <Box display={'flex'} alignItems={'start'} flexDirection={'column'} gap={.5}>
                                                        <Box display={'flex'} alignItems={'center'} gap={1.5}>
                                                            <Typography variant="subtitle2" color="text.primary">
                                                                {value.file_name}
                                                            </Typography>
                                                            {
                                                                isCurrent(value.file_id) && (
                                                                    <StatusChip label="Current" color="green" variant="filled" />
                                                                )
                                                            }
                                                            <Typography variant="caption" color="text.secondary">
                                                                Version {value.version_id}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Uploaded on {formatDate(value.created_date)} by {value.assigned_to} | Expiry : {formatDate(value.expiry_date)} : 6:00PM
                                                        </Typography>
                                                    </Box>
                                                    <IconButton sx={{ ml: 'auto' }} onClick={() => {
                                                        const data = {
                                                            type: value.file_type,
                                                            name: value.file_name,
                                                            s3_path: value.s3_path,
                                                        }
                                                        handleDownload(data)
                                                    }}>
                                                        <FileDownloadOutlinedIcon />
                                                    </IconButton>
                                                </Box>
                                            </Paper>
                                        ))
                                }
                            </Box>
                            <Box display={'flex'} flexShrink={0} flexDirection={'column'} gap={2}>
                                <DataLayout title="Upload New Version" bgVariant="filled">
                                    <Box p={2}>
                                        <FileUpload
                                            type={"artefact"}
                                            ref={fileUploadRef}
                                            onUploadComplete={(file_id: string[]) => onApproved({ "expiry_date": expiryDate, file_id: file_id })}
                                            typeId={artefactDetails.id}
                                            isHideFiles={true}
                                            isAllowMultiple={false}
                                            filesAllowed={['pdf', 'docx', 'csv', 'xlsx']}
                                            isRequired={true}
                                        />
                                    </Box>
                                </DataLayout>
                                <FieldControl
                                    keyName={'expiryDate'}
                                    label={"Expiry Date"}
                                    variant={"datetime"}
                                    value={expiryDate}
                                    onChange={(_key: string, value: Date) => {
                                        setExpiryDate(value)
                                    }}
                                />
                                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
                                    <Button variant="outlined" onClick={() => setExpiryDate(null)}>Clear</Button>
                                    <Button
                                        variant="contained"
                                        disabled={!expiryDate || editArtefacts.isPending}
                                        onClick={() => {
                                            onUpdateVersion();
                                        }}
                                        loading={editArtefacts.isPending}
                                    >
                                        Save changes
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </TabPanel>
                    <TabPanel value="log" sx={{ pl: 2, py: 2, pr: 0, height: "100%" }}>
                        <Log topic={["ARTEFACT"]} sub_event={[]} object_id={artefactDetails.id} parent_id={""} />
                    </TabPanel>
                </Box>
            </TabContext>
            <DrawerControl
                title={"Create/Edit Artefact Requirement"}
                subTitle={""}
                isOpen={editStatus.status}
                handleClose={() => setEditStatus({ status: false, id: null, projectId: null })}
                children={
                    <CreateArtefacts
                        projectId={String(editStatus.projectId)}
                        isEdit={editStatus.status}
                        artefactId={editStatus.id}
                        onClose={() => setEditStatus({ status: false, id: null, projectId: null })}
                        onSave={() => {
                            setEditStatus({ status: false, id: null, projectId: null });
                            refetchArtefacts();
                        }}
                    />
                }
            />
        </Box>
    );
};